'use client';

import { CostcoGroceryLabel } from '@/components/CostcoGroceryLabel';
import {
    A11Y_FILTER_DESCRIBED_BY,
    GRS_BOPIM_FACET_KEY,
    GRS_BOPIW_FACET_KEY,
    GRS_BUY_IN_WAREHOUSE_FACET_KEY,
    LW_BOPIM_FACET_KEY,
    LW_BOPIW_FACET_KEY,
    TWO_DAY_FACET_VALUE,
} from '@/components/SearchResultsFacets/constants';
import {
    StyledList,
    StyledListItem,
} from '@/components/SearchResultsFacets/styles';
import {
    checkRefinementApplied,
    getFacetLabel,
} from '@/components/SearchResultsFacets/utils';
import {
    BOPIM_PICK_UP_TEXT_KEY,
    BUY_IN_WAREHOUSE_TEXT_KEY,
    PICK_UP_TEXT_KEY,
} from '@/constants/index';
import { getKeyValue } from '@/services/content/data';
import { SearchServiceProps } from '@/services/search/types';
import {
    FILTER,
    FILTER_REMOVE,
    FILTER_TYPE_LOCATION,
    FILTER_VAL,
    TRACK_TYPE_MULTI_ATTR,
} from '@/src/components/Analytics/constants';
import { FacetNormalized } from '@/src/components/SearchResultsFacets/types';
import useRefinement from '@/src/hooks/useRefinement';
import { Box } from '@mui/material';
import { visuallyHidden } from '@mui/utils';
import { Checkbox } from '@nxt-costco-com/forge-components';
import { ColorCostcoBlue } from '@nxt-costco-com/forge-design-tokens';
import {
    Analytics,
    type ContentStackEntryDataProps,
    type LocaleProps,
    useQueryParams,
} from '@nxt-costco-com/forge-digital-components';

export function ProgramFilterView({
    facet,
    facetConfig,
    resultsText,
    locale,
    searchService,
}: {
    facet: FacetNormalized;
    facetConfig: ContentStackEntryDataProps;
    resultsText: string;
    locale: LocaleProps;
    searchService: SearchServiceProps;
}) {
    const { handleDoRefine } = useRefinement({ searchService, facetConfig });
    const { queryParams: searchParams } = useQueryParams();

    return (
        <StyledList>
            {facet.buckets.map(bucket => {
                let label = getFacetLabel(
                    bucket.val.toString(),
                    facetConfig,
                    searchService,
                );
                if (
                    (facet.facetKey === GRS_BOPIM_FACET_KEY ||
                        facet.facetKey === LW_BOPIM_FACET_KEY) &&
                    bucket.count > 0
                ) {
                    label = getKeyValue(BOPIM_PICK_UP_TEXT_KEY, facetConfig)!;
                }

                if (
                    facet.facetKey === GRS_BUY_IN_WAREHOUSE_FACET_KEY &&
                    bucket.count > 0
                ) {
                    label = getKeyValue(
                        BUY_IN_WAREHOUSE_TEXT_KEY,
                        facetConfig,
                    )!;
                }

                if (
                    (facet.facetKey === GRS_BOPIW_FACET_KEY ||
                        facet.facetKey === LW_BOPIW_FACET_KEY) &&
                    bucket.count > 0
                ) {
                    label = getKeyValue(PICK_UP_TEXT_KEY, facetConfig)!;
                }

                const filterName = label || undefined;
                if (label === null) {
                    return null;
                }

                if (
                    bucket.val?.toString()?.toLowerCase() ===
                    TWO_DAY_FACET_VALUE?.toLowerCase()
                ) {
                    // 2-day delivery in Canada is CostcoGrocery and has special labeling
                    if (
                        label === 'CostcoGrocery' ||
                        label === 'Épicerie Costco'
                    ) {
                        label = (
                            <CostcoGroceryLabel
                                locale={locale}
                                facetConfig={facetConfig}
                            />
                        ) as unknown as string;
                    } else {
                        label = (
                            <Box
                                sx={{ fontWeight: 800, color: ColorCostcoBlue }}
                            >
                                {label}
                            </Box>
                        ) as unknown as string;
                    }
                }

                const isChecked = checkRefinementApplied({
                    facet,
                    bucket,
                    searchParams,
                    searchService,
                    facetConfig,
                });
                return (
                    <StyledListItem
                        key={bucket.val.toString()}
                        data-private={bucket.leads_to_fsa_chdi}
                        data-bi-private={bucket.leads_to_fsa_chdi}
                    >
                        <Box>
                            <Analytics
                                analyticData={{
                                    trackedData: [
                                        {
                                            key: isChecked
                                                ? FILTER_REMOVE
                                                : FILTER,
                                            value: FILTER_TYPE_LOCATION,
                                        },
                                        {
                                            key: FILTER_VAL,
                                            value: filterName ?? '',
                                        },
                                    ],
                                    trackType: TRACK_TYPE_MULTI_ATTR,
                                    doNotTrack: bucket.leads_to_fsa_chdi,
                                }}
                            >
                                <Checkbox
                                    ariaDescribeText={A11Y_FILTER_DESCRIBED_BY}
                                    onChange={event =>
                                        handleDoRefine(
                                            facet,
                                            bucket,
                                            event.target.checked,
                                        )
                                    }
                                    label={
                                        (
                                            <>
                                                {label}{' '}
                                                <Box sx={visuallyHidden}>
                                                    {`${bucket.count?.toLocaleString()} ${resultsText}`}
                                                </Box>
                                            </>
                                        ) as unknown as string
                                    }
                                    isChecked={!!isChecked}
                                    id={`${facet.facetKey}-${bucket.val}`}
                                />
                            </Analytics>
                        </Box>
                        <Box aria-hidden>
                            ({bucket.count?.toLocaleString()})
                        </Box>
                    </StyledListItem>
                );
            })}
        </StyledList>
    );
}
